"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.transformOperation = void 0;
const csvtojson_1 = __importDefault(require("csvtojson"));
const _ = __importStar(require("lodash"));
const common_1 = require("@finley-technologies/common");
const consolidateErrors_1 = require("./consolidateErrors");
const errorUtils_1 = require("./errorUtils");
const helpers_1 = require("./helpers");
const typeguards_1 = require("./typeguards");
const utils_1 = require("./utils");
const consolidateFlattenValidationErrors = (unknownErrors) => {
    return (0, consolidateErrors_1.consolidateErrors)(unknownErrors, (error) => error.field, // Group by field
    (error) => ({
        row: String(error.row),
        value: error.value,
        expectedType: error.expectedType,
        expectedFormat: error.expectedFormat,
    }), // Map to row data
    (field) => `Field [${field}] has invalid values`);
};
const consolidateToStructuredErrors = (errors) => {
    const flattenValidationErrors = errors.filter((error) => error.type === 'flattenValidation');
    // we want to consolidate the errors if there are too many of those.
    return [...consolidateFlattenValidationErrors(flattenValidationErrors)];
};
const aggregations = {
    sum: {
        initialValue: 0,
        reducer: (accumulator, newValue) => {
            (0, common_1.finleyAssert)(_.isNumber(accumulator), 'sum aggregation expects numbers');
            if (_.isNil(newValue)) {
                return accumulator;
            }
            (0, common_1.finleyAssert)(_.isNumber(newValue), 'sum aggregation expects numbers');
            return accumulator + newValue;
        },
        transformType: (inputType) => {
            // Only support numeric fields. Support optional fields by treating them as having zero value.
            switch (inputType) {
                case 'integer':
                case 'optionalInteger':
                    return 'integer';
                case 'float':
                case 'optionalFloat':
                    return 'float';
                default:
                    return undefined;
            }
        },
    },
};
const createAccumulatorMap = (aggregationFields) => {
    // Map preserves the order of `aggregationFields`.
    return new Map(aggregationFields.map(({ outputField, aggregationFunction }) => {
        return [outputField, aggregations[aggregationFunction].initialValue];
    }));
};
/** Creates a Map keyed by field name. */
const createFieldConfigMap = (fieldConfigs) => {
    return new Map(fieldConfigs.map((field) => [field.field, field]));
};
const validateTransform = ({ groupByField: groupByFieldName, aggregationFields, srcDataTape, }) => {
    const outputFields = [];
    const errors = [];
    const srcFields = createFieldConfigMap(srcDataTape.baseFields);
    // Group by field must exist and have a supported type.
    const groupByField = srcFields.get(groupByFieldName);
    if (groupByField === undefined) {
        errors.push({
            type: 'metadataValidation',
            message: `group by field ${groupByFieldName} does not exist in the input`,
        });
    }
    else {
        switch (groupByField.type) {
            // Floating point fields are problematic for equality purposes.
            // Grouping by optionals can be easily added if there's a need.
            // Grouping by sanitized dates and times would also be easy.
            case 'boolean':
            case 'integer':
            case 'string':
                // `getSanitizedConfig` because we read values using `getSanitizedDataTapeField`.
                // The resulting sanitized value is passed directly to the output, so we should
                // ensure any sanitization that happens is reflected in the field config.
                outputFields.push((0, helpers_1.getSanitizedConfig)({
                    configBeforeSanitize: groupByField,
                    // we do not want to field names to be standardized.
                    // they are already sanitized (or not) by sanitize operation as appropriate.
                    sanitizeFieldNames: false,
                }));
                break;
            default:
                errors.push({
                    type: 'metadataValidation',
                    message: `group by field ${groupByField.field} has type ${groupByField.type}, but should be boolean, integer, or string`,
                });
                break;
        }
    }
    const uniqueOutputFields = createAccumulatorMap(aggregationFields).size;
    if (uniqueOutputFields !== aggregationFields.length) {
        errors.push({
            type: 'metadataValidation',
            message: 'aggregation outputField names must be unique',
        });
    }
    aggregationFields.forEach(({ aggregationFunction, inputField, outputField }) => {
        const srcField = srcFields.get(inputField);
        if (srcField === undefined) {
            errors.push({
                type: 'metadataValidation',
                message: `inputField ${inputField} does not exist in the input`,
            });
            return;
        }
        if (outputField === groupByFieldName) {
            errors.push({
                type: 'metadataValidation',
                message: `outputField ${outputField} cannot be same as group by field`,
            });
            return;
        }
        // `getSanitizedConfig` because we read values using `getSanitizedDataTapeField`.
        // The resulting sanitized value is what gets passed to the implementation of the
        // `Aggregation` interface, so we should ensure any sanitization that happens is
        // reflected in the argument to `transformType`.
        // we do not want getSanitizedConfig to modify field names just the type.
        const sanitizedFieldConfig = (0, helpers_1.getSanitizedConfig)({
            configBeforeSanitize: srcField,
            // we do not want to field names to be standardized.
            // they are already sanitized (or not) by sanitize operation as appropriate.
            sanitizeFieldNames: false,
        });
        const outputType = aggregations[aggregationFunction].transformType(sanitizedFieldConfig.type);
        switch (outputType) {
            case undefined:
                errors.push({
                    type: 'metadataValidation',
                    message: `aggregation function '${aggregationFunction}' does not accept inputField ${srcField.field} of type ${srcField.type}`,
                });
                break;
            case 'boolean':
                outputFields.push({
                    type: 'boolean',
                    externalBooleanTrue: 'true',
                    field: outputField,
                });
                break;
            case 'string':
            case 'integer':
            case 'float':
                outputFields.push({ type: outputType, field: outputField });
                break;
            default:
                (0, common_1.finleyAssertUnreachable)(outputType);
        }
    });
    if (errors.length) {
        return { isValid: false, errors };
    }
    return {
        isValid: true,
        data: {
            baseFields: outputFields,
            delimiter: ',',
            sanitizedFieldNames: false,
        },
    };
};
// This might be worth making available as a utility!
const floatFormatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 10, // 10 digits of precision after the decimal point, same as our databases.
    useGrouping: false, // no thousands-separator commas
});
const toCsvField = (type, value) => {
    switch (type) {
        case 'string':
            (0, common_1.finleyAssert)(_.isString(value), 'expected string AggregationOutputType');
            return value;
        case 'boolean':
            (0, common_1.finleyAssert)(_.isBoolean(value), 'expected boolean AggregationOutputType');
            return _.toString(value);
        case 'integer':
            (0, common_1.finleyAssert)(_.isSafeInteger(value), 'AggregationOutputType is not an integer, or cannot be precisely represented');
            return _.toString(value);
        case 'float':
            (0, common_1.finleyAssert)(_.isNumber(value) && _.isFinite(value), 'AggregationOutputType is not a float');
            return floatFormatter.format(value);
        default:
            return (0, common_1.finleyAssertUnreachable)(type);
    }
};
/**
 * It is assumed that the checks in {@link getOutputDataTape} have
 * already been performed. Assumes input sorted on groupByField.
 */
const createAccumulator = (transformation, outputTapeDefinition) => {
    const inputFields = createFieldConfigMap(transformation.srcDataTape.baseFields);
    const outputFields = createFieldConfigMap(outputTapeDefinition.baseFields);
    const groupByField = inputFields.get(transformation.groupByField);
    const outputData = [];
    const errors = [];
    let lastGroupByValue;
    let accumulatorMap = createAccumulatorMap(transformation.aggregationFields);
    let accumulatedRows = 0;
    const maybeWriteOutputRow = () => {
        if (accumulatedRows > 0) {
            const output = {};
            output[groupByField.field] = _.toString(lastGroupByValue);
            accumulatorMap.forEach((value, fieldName) => {
                const outputType = outputFields.get(fieldName)
                    .type;
                output[fieldName] = toCsvField(outputType, value);
            });
            outputData.push(output);
            accumulatorMap = createAccumulatorMap(transformation.aggregationFields);
            accumulatedRows = 0;
        }
    };
    return {
        getResult: () => {
            maybeWriteOutputRow(); // handle any remaining accumulated values
            return { errors, outputData };
        },
        accumulate: (rowData, rowIdx) => {
            const currentGroupByValue = (0, helpers_1.getSanitizedDataTapeField)(groupByField, rowData[transformation.groupByField]);
            if (currentGroupByValue !== lastGroupByValue) {
                maybeWriteOutputRow();
                lastGroupByValue = currentGroupByValue;
            }
            transformation.aggregationFields.forEach(({ inputField, outputField, aggregationFunction }) => {
                let newValue;
                try {
                    newValue = (0, helpers_1.getSanitizedDataTapeField)(inputFields.get(inputField), rowData[inputField]);
                }
                catch (err) {
                    if ((0, typeguards_1.isInvalidField)(err)) {
                        errors.push({
                            type: 'flattenValidation',
                            message: `Expected type ${err.expectedType} but got [${err.value}]`,
                            field: err.name,
                            row: rowIdx,
                            expectedType: err.expectedType,
                            value: err.value,
                            expectedFormat: err.expectedFormat,
                        });
                    }
                    else {
                        throw err;
                    }
                }
                const newAccumulatedValue = aggregations[aggregationFunction].reducer(accumulatorMap.get(outputField), newValue);
                accumulatorMap.set(outputField, newAccumulatedValue);
            });
            accumulatedRows += 1;
        },
    };
};
const performTransform = async ({ dataTapeBuffers, transformation, }) => {
    try {
        const tapeDefinitionResult = validateTransform(transformation);
        (0, common_1.finleyAssert)(tapeDefinitionResult.isValid, 'Invalid tape definition');
        const outputDataTape = tapeDefinitionResult.data;
        (0, common_1.finleyAssert)(outputDataTape !== null, 'Expected outputDataTape to be defined');
        (0, common_1.finleyAssert)(dataTapeBuffers.length === 1, 'Expected 1 data tape buffer');
        const srcDataTapeBuffer = dataTapeBuffers[0];
        const srcDataTape = transformation.srcDataTape;
        const csvString = common_1.finleyBuffer.toString(srcDataTapeBuffer);
        const delimiter = srcDataTape.delimiter;
        const headers = await (0, helpers_1.getHeaders)({
            csvString,
            delimiter,
            sanitizeFieldNames: false, // field names are sanitized previously by sanitize op
        });
        // Sort by groupByField so that we only have to track one groupByField at a time.
        // For predictability and repeatability, this must be a stable sort algorithm.
        //
        // Since this is all in memory, we could equivalently skip the sort, and track
        // all groupByFields until we reach the end of file.
        const sortedInput = _.sortBy(await (0, csvtojson_1.default)({ headers, delimiter }).fromString(csvString), (row) => row[transformation.groupByField]);
        const accumulator = createAccumulator(transformation, outputDataTape);
        sortedInput.forEach(accumulator.accumulate);
        const { errors, outputData } = accumulator.getResult();
        if (errors.length) {
            return { isValid: false, errors: consolidateToStructuredErrors(errors) };
        }
        const outputBuffer = (0, utils_1.rowDataToCsv)(outputData, outputDataTape);
        return { isValid: true, data: { outputBuffer }, supplementals: [] };
    }
    catch (error) {
        return (0, errorUtils_1.convertErrorToTapeFailureResult)(error, 'flatten: ');
    }
};
exports.transformOperation = {
    operation: 'flatten',
    validateTransform,
    performTransform,
};
//# sourceMappingURL=flatten-transform.js.map